import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Calendar, 
  Search, 
  Download,
  Clock,
  BookOpen,
  Award,
  CreditCard,
  History,
  ArrowUpRight,
  Plus
} from 'lucide-react';

interface CreditTransaction {
  id: number;
  amount: number;
  transactionType: string;
  description: string;
  createdAt: string;
  user: {
    id: number;
    username: string;
    fullName: string;
  };
  sessionRequest?: {
    id: number;
    skill: {
      name: string;
    };
  };
  learningSession?: {
    id: number;
    skill: {
      name: string;
    };
  };
}

interface CreditStats {
  totalEarnings: number;
  totalSpendings: number;
  currentBalance: number;
  totalTransactions: number;
}

const CreditHistory: React.FC = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<string>('all');
  const [stats, setStats] = useState<CreditStats>({
    totalEarnings: 0,
    totalSpendings: 0,
    currentBalance: 0,
    totalTransactions: 0
  });

  useEffect(() => {
    if (user) {
      fetchCreditHistory();
    }
  }, [user]);

  const fetchCreditHistory = async () => {
    try {
      setIsLoading(true);
      const [transactionsResponse, userProfileResponse] = await Promise.all([
        apiService.getCreditTransactions(),
        apiService.getUserProfile()
      ]);

      const fetchedTransactions = transactionsResponse.data || [];
      setTransactions(fetchedTransactions);

      // Calculate stats
      const earnings = fetchedTransactions
        .filter(t => t.amount > 0)
        .reduce((sum, t) => sum + t.amount, 0);
      
      const spendings = fetchedTransactions
        .filter(t => t.amount < 0)
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);

      setStats({
        totalEarnings: earnings,
        totalSpendings: spendings,
        currentBalance: userProfileResponse.data?.credits || 0,
        totalTransactions: fetchedTransactions.length
      });
    } catch (error: any) {
      toast.error('Failed to load credit history');
      console.error('Error fetching credit history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTransactionTypeInfo = (transaction: CreditTransaction) => {
    const amount = transaction.amount;
    const isPositive = amount > 0;
    
    switch (transaction.transactionType) {
      case 'HOLD':
        return {
          icon: <Clock className="w-4 h-4" />,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          label: 'Credits Held',
          description: 'Temporarily held for session request'
        };
      case 'RELEASE':
        return {
          icon: <ArrowUpRight className="w-4 h-4" />,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          label: 'Credits Released',
          description: 'Returned from held session request'
        };
      case 'TRANSFER':
        return {
          icon: isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />,
          color: isPositive ? 'text-green-600' : 'text-red-600',
          bgColor: isPositive ? 'bg-green-50' : 'bg-red-50',
          label: isPositive ? 'Credits Earned' : 'Credits Spent',
          description: isPositive ? 'Earned from teaching session' : 'Spent on learning session'
        };
      case 'TOP_UP':
        return {
          icon: <Plus className="w-4 h-4" />,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          label: 'Credits Added',
          description: 'Purchased credits'
        };
      case 'ADJUSTMENT':
        return {
          icon: <Award className="w-4 h-4" />,
          color: 'text-purple-600',
          bgColor: 'bg-purple-50',
          label: 'Credits Adjusted',
          description: 'Admin adjustment'
        };
      default:
        return {
          icon: <CreditCard className="w-4 h-4" />,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          label: 'Credit Transaction',
          description: 'Credit activity'
        };
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesType = filterType === 'all' || 
      (filterType === 'earnings' && transaction.amount > 0) ||
      (filterType === 'spendings' && transaction.amount < 0);
    
    const matchesSearch = transaction.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         transaction.transactionType.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesDate = dateRange === 'all' || 
      (dateRange === 'today' && isToday(transaction.createdAt)) ||
      (dateRange === 'week' && isThisWeek(transaction.createdAt)) ||
      (dateRange === 'month' && isThisMonth(transaction.createdAt));
    
    return matchesType && matchesSearch && matchesDate;
  });

  const isToday = (dateString: string) => {
    const today = new Date();
    const date = new Date(dateString);
    return date.toDateString() === today.toDateString();
  };

  const isThisWeek = (dateString: string) => {
    const today = new Date();
    const date = new Date(dateString);
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    return date >= weekAgo;
  };

  const isThisMonth = (dateString: string) => {
    const today = new Date();
    const date = new Date(dateString);
    return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount: number) => {
    const isPositive = amount > 0;
    return (
      <span className={`font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? '+' : ''}{amount} credits
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4 pulse-glow"></div>
          <h2 className="text-xl font-bold gradient-text">Loading Credit History...</h2>
          <p className="text-gray-600 text-lg">Preparing your financial data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold gradient-text">Credit History</h1>
              <p className="text-gray-600 mt-3 text-lg">Track your earnings, spendings, and credit transactions</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Current Balance</p>
                <p className="text-3xl font-bold text-blue-600">{stats.currentBalance} credits</p>
              </div>
              <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
                <CreditCard className="w-10 h-10 text-white" />
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200 hover:shadow-md transition-all duration-300">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-green-600 font-medium">Total Earnings</p>
                  <p className="text-2xl font-bold text-green-700">{stats.totalEarnings} credits</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-50 to-pink-50 p-6 rounded-xl border border-red-200 hover:shadow-md transition-all duration-300">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
                  <TrendingDown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-red-600 font-medium">Total Spendings</p>
                  <p className="text-2xl font-bold text-red-700">{stats.totalSpendings} credits</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-teal-50 p-6 rounded-xl border border-blue-200 hover:shadow-md transition-all duration-300">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                  <History className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Transactions</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.totalTransactions}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-50 to-blue-50 p-6 rounded-xl border border-teal-200 hover:shadow-md transition-all duration-300">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center shadow-lg">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-teal-600 font-medium">Net Position</p>
                  <p className={`text-2xl font-bold ${stats.totalEarnings - stats.totalSpendings >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {stats.totalEarnings - stats.totalSpendings} credits
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Transactions</option>
                <option value="earnings">Earnings Only</option>
                <option value="spendings">Spendings Only</option>
              </select>

              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>

              <button
                onClick={() => {
                  // Export functionality could be added here
                  toast.success('Export feature coming soon!');
                }}
                className="flex items-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>

        {/* Transactions List */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Transaction History</h2>
            <p className="text-sm text-gray-500">
              Showing {filteredTransactions.length} of {transactions.length} transactions
            </p>
          </div>

          {filteredTransactions.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <History className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-lg">No transactions found</p>
              <p className="text-gray-400">Try adjusting your filters or search terms</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTransactions.map((transaction) => {
                const typeInfo = getTransactionTypeInfo(transaction);
                return (
                  <div
                    key={transaction.id}
                    className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 ${typeInfo.bgColor} rounded-full flex items-center justify-center`}>
                          <div className={typeInfo.color}>
                            {typeInfo.icon}
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex items-center space-x-2">
                            <h3 className="font-medium text-gray-900">{typeInfo.label}</h3>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${typeInfo.bgColor} ${typeInfo.color}`}>
                              {transaction.transactionType}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{typeInfo.description}</p>
                          
                          {transaction.description && (
                            <p className="text-sm text-gray-500 mt-1">{transaction.description}</p>
                          )}
                          
                          {/* Show related session info if available */}
                          {(transaction.sessionRequest || transaction.learningSession) && (
                            <div className="flex items-center space-x-2 mt-2">
                              <BookOpen className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-500">
                                {transaction.sessionRequest?.skill?.name || transaction.learningSession?.skill?.name}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="mb-2">
                          {formatAmount(transaction.amount)}
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDateTime(transaction.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditHistory;
